import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { apiClient, ApiClientError } from '@/lib/api/client'
import type { HelmNotification, HelmNotificationList } from '@/lib/api/types'
import { cn } from '@/lib/utils'

const REFRESH_MS = 10_000

function canSynchronize() {
  return document.visibilityState === 'visible' && navigator.onLine
}

function combined(items: HelmNotification[]) {
  // Keep server order: its cursor retains timestamp precision beyond JavaScript dates.
  return [...new Map(items.map((item) => [item.id, item])).values()]
}

function allowed(items: HelmNotification[], whatsappAccess?: boolean) {
  return whatsappAccess === false
    ? items.filter((item) => !item.kind.startsWith('WHATSAPP_'))
    : items
}

// Severity reads like a status: urgent is the one red, attention is amber.
const SEVERITY_DOT: Record<HelmNotification['severity'], string> = {
  URGENT: 'bg-blocked',
  ATTENTION: 'bg-waiting',
  INFO: 'bg-muted-foreground',
}

/** Business time is Kampala time, wherever the reader is. */
function when(iso: string): string {
  const then = new Date(iso)
  const minutes = Math.round((Date.now() - then.getTime()) / 60_000)
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes} min ago`
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)} h ago`
  return then.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Africa/Kampala',
  })
}

/**
 * The server owns notification history and read state. Opening a notification
 * marks it read without removing it; polling only runs in a visible, online tab.
 */
export function NotificationBell() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [list, setList] = useState<HelmNotificationList | null>(null)
  const [failed, setFailed] = useState(false)
  const [readError, setReadError] = useState<string | null>(null)
  const [fetching, setFetching] = useState(false)
  const [loadingOlder, setLoadingOlder] = useState(false)
  const [saving, setSaving] = useState(false)
  const mounted = useRef(false)
  const inFlight = useRef(false)
  const mutation = useRef(false)
  const revision = useRef(0)
  const queued = useRef(false)
  const headIds = useRef(new Set<string>())
  const headFingerprint = useRef('')
  const paginationAnchor = useRef<string | null>(null)

  const refresh = useCallback(async function refreshHead() {
    if (!mounted.current || !canSynchronize()) return
    if (inFlight.current || mutation.current) {
      queued.current = true
      return
    }
    inFlight.current = true
    setFetching(true)
    const started = revision.current
    try {
      const next = await apiClient.listNotifications()
      if (mounted.current && started === revision.current) {
        const fingerprint = next.items.map((item) => item.id).join('|')
        const headChanged = fingerprint !== headFingerprint.current
        const previousHeadIds = headIds.current
        const nextIds = new Set(next.items.map((item) => item.id))
        headFingerprint.current = fingerprint
        headIds.current = nextIds
        const oldest = next.items.at(-1)
        if (headChanged) paginationAnchor.current = oldest?.id ?? null
        setList((current) => ({
          ...next,
          // Keep loaded earlier pages, but honor removals within the refreshed head and access revocation.
          items: combined(
            allowed(
              [
                ...next.items,
                ...(next.has_more && oldest
                  ? (current?.items.filter(
                      (item) =>
                        !nextIds.has(item.id) &&
                        (!previousHeadIds.has(item.id) ||
                          item.last_raised_at <= oldest.last_raised_at),
                    ) ?? [])
                  : []),
              ],
              next.whatsapp_access,
            ),
          ),
          has_more: headChanged
            ? Boolean(next.has_more)
            : (current?.has_more ?? Boolean(next.has_more)),
          next_cursor: headChanged
            ? (next.next_cursor ?? null)
            : (current?.next_cursor ?? next.next_cursor ?? null),
        }))
        setFailed(false)
      }
    } catch (error) {
      if (mounted.current && started === revision.current) {
        if (error instanceof ApiClientError && [401, 403].includes(error.status)) {
          setList(null)
          headFingerprint.current = ''
          headIds.current.clear()
          paginationAnchor.current = null
        }
        setFailed(true)
      }
    } finally {
      inFlight.current = false
      if (mounted.current) setFetching(false)
      if (queued.current) {
        queued.current = false
        void refreshHead()
      }
    }
  }, [])

  useEffect(() => {
    mounted.current = true
    void refresh()
    const timer = window.setInterval(() => void refresh(), REFRESH_MS)
    const onReturn = () => void refresh()
    window.addEventListener('focus', onReturn)
    window.addEventListener('online', onReturn)
    document.addEventListener('visibilitychange', onReturn)
    return () => {
      mounted.current = false
      window.clearInterval(timer)
      window.removeEventListener('focus', onReturn)
      window.removeEventListener('online', onReturn)
      document.removeEventListener('visibilitychange', onReturn)
    }
  }, [refresh])

  useEffect(() => {
    if (open) void refresh()
  }, [open, refresh])

  async function openItem(item: HelmNotification) {
    setOpen(false)
    if (item.link_path) navigate(item.link_path)
    if (!item.read) await persistRead(item.id)
  }

  async function persistRead(id?: string) {
    if (mutation.current) return
    mutation.current = true
    revision.current++
    setSaving(true)
    setReadError(null)
    try {
      if (id) await apiClient.markNotificationRead(id)
      else await apiClient.markAllNotificationsRead()
      if (mounted.current)
        setList((current) => {
          if (!current) return current
          const changed = current.items.filter(
            (item) => !item.read && (!id || item.id === id),
          ).length
          return {
            ...current,
            items: current.items.map((item) =>
              !id || item.id === id ? { ...item, read: true } : item,
            ),
            unread_count: id ? Math.max(0, current.unread_count - changed) : 0,
          }
        })
    } catch {
      if (mounted.current)
        setReadError("Couldn't save read status. These notifications remain unread; try again.")
    } finally {
      mutation.current = false
      if (mounted.current) setSaving(false)
      queued.current = false
      void refresh()
    }
  }

  async function loadEarlier() {
    const cursor = list?.next_cursor
    if (!cursor || inFlight.current || mutation.current || !canSynchronize()) return
    inFlight.current = true
    setFetching(true)
    setLoadingOlder(true)
    const started = revision.current
    try {
      const next = await apiClient.listNotifications({ cursor })
      if (mounted.current && started === revision.current) {
        const anchor = paginationAnchor.current
        paginationAnchor.current = next.items.at(-1)?.id ?? anchor
        setList((current) => {
          const pageIds = new Set(next.items.map((item) => item.id))
          const retained = (current?.items ?? []).filter((item) => !pageIds.has(item.id))
          const anchorIndex = retained.findIndex((item) => item.id === anchor)
          const position = anchorIndex < 0 ? retained.length : anchorIndex + 1
          return {
            ...next,
            items: combined(
              allowed(
                [...retained.slice(0, position), ...next.items, ...retained.slice(position)],
                next.whatsapp_access,
              ),
            ),
          }
        })
        setFailed(false)
      }
    } catch (error) {
      if (mounted.current && started === revision.current) {
        if (error instanceof ApiClientError && [401, 403].includes(error.status)) {
          setList(null)
          headFingerprint.current = ''
          headIds.current.clear()
          paginationAnchor.current = null
        }
        setFailed(true)
      }
    } finally {
      inFlight.current = false
      if (mounted.current) {
        setFetching(false)
        setLoadingOlder(false)
      }
      if (queued.current) {
        queued.current = false
        void refresh()
      }
    }
  }

  const unread = list?.unread_count ?? 0
  const items = list?.items ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
          data-tour="header-notifications"
        >
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1 text-[11px] font-semibold text-brand-foreground"
            >
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(24rem,calc(100vw-1rem))] p-0">
        <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
          <p className="font-semibold">Notifications</p>
          {unread > 0 && (
            <Button
              variant="link"
              size="sm"
              className="h-auto px-0"
              disabled={saving}
              onClick={() => void persistRead()}
            >
              {saving ? 'Saving...' : 'Mark all read'}
            </Button>
          )}
        </div>
        {failed && (
          <div className="space-y-2 border-b px-4 py-3 text-sm" role="status">
            <p className="text-muted-foreground">
              {list
                ? "Couldn't refresh notifications. Showing the last saved notifications."
                : "Couldn't load notifications."}
            </p>
            <Button variant="outline" size="sm" disabled={fetching} onClick={() => void refresh()}>
              Retry
            </Button>
          </div>
        )}
        {readError && (
          <p role="status" className="border-b px-4 py-3 text-sm text-destructive">
            {readError}
          </p>
        )}
        <div className="max-h-[min(28rem,70vh)] overflow-y-auto" role="list">
          {!list && !failed ? (
            <p role="status" className="px-4 py-8 text-center text-sm text-muted-foreground">
              Loading notifications...
            </p>
          ) : items.length === 0 && !failed ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground">
              Nothing needs you right now.
            </p>
          ) : (
            items.map((item) => (
              <button
                key={item.id}
                type="button"
                role="listitem"
                disabled={saving}
                onClick={() => void openItem(item)}
                className={cn(
                  'flex w-full gap-3 border-b px-4 py-3 text-left last:border-b-0 hover:bg-accent focus-visible:bg-accent focus-visible:outline-none',
                  !item.read && 'bg-accent/40',
                )}
              >
                <span
                  aria-hidden
                  className={cn('mt-1.5 size-2 shrink-0 rounded-full', SEVERITY_DOT[item.severity])}
                />
                <span className="min-w-0 flex-1 space-y-0.5">
                  <span className={cn('helm-private block text-sm', !item.read && 'font-semibold')}>
                    {item.title}
                  </span>
                  {item.body && (
                    <span className="helm-private line-clamp-2 block text-sm text-muted-foreground">
                      {item.body}
                    </span>
                  )}
                  <span className="block text-xs text-muted-foreground">
                    {when(item.last_raised_at)}
                    {item.occurrences > 1 ? ` · raised ${item.occurrences} times` : ''}
                  </span>
                </span>
                {!item.read && <span className="sr-only">Unread</span>}
              </button>
            ))
          )}
          {list?.has_more && list.next_cursor && (
            <div className="px-4 py-3">
              <Button
                variant="outline"
                size="sm"
                disabled={fetching || saving}
                onClick={() => void loadEarlier()}
              >
                {loadingOlder ? 'Loading earlier notifications...' : 'Load earlier notifications'}
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
